import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/constant';
import { withMock, mockFail, isMockEnabled, mockDelay, mockOk } from '@/fetch/core/mock';
import { ApiResponse } from '@/types';
import * as Mock from './abnormalDurationMock';

// ============ 类型定义 ============

export type DeviceListData = string[];

export interface StationByVehicleParams {
  vehicleName: string;
}
export interface StationByVehicleData {
  stationBaseId: number;
  stationName: string;
}

export interface CascadeStationItem {
  id: number;
  name: string;
  children: CascadeStationItem[];
}

export type CascadeStationData = CascadeStationItem[];

export interface FilterOption {
  code: string;
  name: string;
}

export interface FiltersData {
  supplier: FilterOption[];
  problemType: FilterOption[];
  recovered: { value: string; label: string }[];
  region: { value: string; label: string }[];
  provinceAgency: { value: string; label: string }[];
}

export interface ProblemType {
  id: number;
  name: string;
}
export interface AddProblemTypeParams {
  name: string;
}

export interface RecordListParams {
  page?: number;
  pageSize?: number;
  vehicleName?: string;
  provinceIds?: number[];
  cityIds?: number[];
  stationBaseIds?: number[];
  region?: string[];
  qlProvinceAgencyCodes?: string[];
  supplier?: string[];
  problemType?: string[];
  recovered?: number;
  hasTrip?: boolean;
  startTime?: string;
  endTime?: string;
  sortField?: string;
  sortOrder?: string;
}

export interface RecordListItem {
  id: number;
  supplier: string;
  region: string;
  province: string;
  city: string;
  stationName: string;
  vehicleName: string;
  startOriginalDate: string;
  problemTypeNames: string;
  problemDetail: string;
  recovered: number;
  endOriginalDate: string | null;
  impactDays: number;
  taskRecord: number;
  source: string;
  modifyUser: string;
  modifyTime: string;
}

export interface RecordListData {
  total: number;
  pages: number;
  pageNum: number;
  pageSize: number;
  list: RecordListItem[];
}

export interface RecordDetailParams {
  recordId: number;
}
export interface RecordDetailData {
  id: number;
  vehicleName: string;
  stationBaseId: number;
  stationName: string;
  supplier: string;
  problemTypes: number[];
  problemDetail: string;
  startOriginalDate: string;
  endOriginalDate: string | null;
}

export interface UnrecoveredCountParams {
  vehicleName: string;
  problemTypeIds: number[];
}

export interface AddRecordParams {
  vehicleName: string;
  stationName: string;
  problemTypeIds: number[];
  problemDetail?: string;
  startTime?: string;
  endTime?: string;
}

export interface EditRecordParams {
  id: number;
  vehicleName: string;
  stationName: string;
  problemTypeIds: number[];
  problemDetail?: string;
  startTime?: string;
  endTime?: string;
}

export interface DeleteRecordParams {
  id: number;
}

export interface TemplateData {
  url: string;
}

export interface ImportUploadParams {
  fileKey: string;
  bucketName: string;
}
export interface ImportUploadData {
  taskNo: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  status: string;
  resultDescription: string;
  failFileUrl: string | null;
  successFileUrl: string | null;
}

export interface ImportLogListParams {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}
export interface ImportLogItem {
  taskId: string;
  fileName: string;
  status: string;
  statusDesc: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  operator: string;
  operateTime: string;
}
export interface ImportLogListData {
  total: number;
  pages: number;
  pageNum: number;
  pageSize: number;
  list: ImportLogItem[];
}

export interface ExportRecordsParams {
  vehicleName?: string;
  provinceIds?: number[];
  cityIds?: number[];
  stationBaseIds?: number[];
  supplier?: string[];
  problemTypeIds?: number[];
  recovered?: number | null;
  hasTrip?: boolean | null;
  startTime?: string | null;
  endTime?: string | null;
  region?: string[];
  qlProvinceAgencyCodes?: string[];
}

export interface ExportRecordsData {
  url: string;
}

// ============ API 类 ============

class AbnormalDurationApi {
  public async getDeviceList(): Promise<ApiResponse<DeviceListData>> {
    return withMock('get_device_list', {}, () => Mock.MOCK_DEVICE_LIST, () =>
      request({ path: '/k2/management/device/get_device_list', method: Method.POST })
    );
  }

  public async getStationCascadeList(): Promise<ApiResponse<CascadeStationData>> {
    return request({ path: '/k2/management/common/get_cascade_station_address_list', method: Method.POST, body: {} });
  }

  public async getStationByVehicle(
    params: StationByVehicleParams
  ): Promise<ApiResponse<StationByVehicleData>> {
    return withMock('station/by-vehicle', params, () => Mock.mockGetStationByVehicle(params.vehicleName), () =>
      request({ path: '/k2/management/device_disabled_record/station/by-vehicle', method: Method.POST, body: params })
    );
  }

  public async getFilters(): Promise<ApiResponse<FiltersData>> {
    return withMock('records/filters', {}, () => Mock.MOCK_FILTERS, () =>
      request({ path: '/k2/management/device_disabled_record/records/filters', method: Method.POST })
    );
  }

  public async getProblemTypes(): Promise<ApiResponse<ProblemType[]>> {
    return withMock('problem-types/list', {}, () => Mock.mockGetProblemTypes(), () =>
      request({ path: '/k2/management/device_disabled_record/problem-types/list', method: Method.POST })
    );
  }

  public async addProblemType(
    params: AddProblemTypeParams
  ): Promise<ApiResponse<null>> {
    if (isMockEnabled()) {
      await mockDelay();
      const result = Mock.mockAddProblemType(params.name);
      const res = result.success ? mockOk(null) : mockFail(result.message || '创建失败');
      console.groupCollapsed('%c[Mock] problem-types/add', 'color: #faad14; font-weight: bold');
      console.log('请求参数:', params);
      console.log('响应数据:', res);
      console.groupEnd();
      return res;
    }
    return request({ path: '/k2/management/device_disabled_record/problem-types/add', method: Method.POST, body: params });
  }

  public async getRecordList(
    params: RecordListParams
  ): Promise<ApiResponse<RecordListData>> {
    return withMock('records/list', params, () => Mock.mockGetRecordList(params), () =>
      request({ path: '/k2/management/device_disabled_record/records/list', method: Method.POST, body: params })
    );
  }

  public async getRecordDetail(
    params: RecordDetailParams
  ): Promise<ApiResponse<RecordDetailData>> {
    return withMock('records/detail', params, () => Mock.mockGetRecordDetail(params.recordId), () =>
      request({ path: '/k2/management/device_disabled_record/records/detail', method: Method.POST, body: params })
    );
  }

  public async getUnrecoveredCount(
    params: UnrecoveredCountParams
  ): Promise<ApiResponse<number>> {
    return withMock(
      'records/unrecovered-count',
      params,
      () => Mock.mockGetUnrecoveredCount(params),
      () =>
        request({
          path: '/k2/management/device_disabled_record/records/unrecovered-count',
          method: Method.POST,
          body: params,
        })
    );
  }

  public async addRecord(
    params: AddRecordParams
  ): Promise<ApiResponse<null>> {
    return withMock('records/add', params, () => { Mock.mockAddRecord(params); return null; }, () =>
      request({ path: '/k2/management/device_disabled_record/records/add', method: Method.POST, body: params })
    );
  }

  public async editRecord(
    params: EditRecordParams
  ): Promise<ApiResponse<null>> {
    return withMock('records/edit', params, () => { Mock.mockEditRecord(params); return null; }, () =>
      request({ path: '/k2/management/device_disabled_record/records/edit', method: Method.POST, body: params })
    );
  }

  public async deleteRecord(
    params: DeleteRecordParams
  ): Promise<ApiResponse<null>> {
    return withMock('records/delete', params, () => { Mock.mockDeleteRecord(params.id); return null; }, () =>
      request({ path: '/k2/management/device_disabled_record/records/delete', method: Method.POST, body: params })
    );
  }

  public async exportRecords(
    params: ExportRecordsParams
  ): Promise<ApiResponse<ExportRecordsData>> {
    return withMock(
      'records/export',
      params,
      () => ({ url: 'https://example.com/export.xlsx' }),
      () =>
        request({
          path: '/k2/management/device_disabled_record/records/export',
          method: Method.POST,
          body: params,
        })
    );
  }

  public async getImportTemplate(): Promise<ApiResponse<TemplateData>> {
    return withMock('import/template', {}, () => Mock.MOCK_IMPORT_TEMPLATE, () =>
      request({ path: '/k2/management/device_disabled_record/import/template', method: Method.POST, body: { type: 1 } })
    );
  }

  public async importUpload(
    params: ImportUploadParams
  ): Promise<ApiResponse<ImportUploadData>> {
    return withMock('import/upload', params, () => Mock.MOCK_IMPORT_UPLOAD, () =>
      request({ path: '/k2/management/device_disabled_record/import/upload', method: Method.POST, body: params })
    );
  }

  public async getImportLogList(
    params: ImportLogListParams
  ): Promise<ApiResponse<ImportLogListData>> {
    return withMock('import/logs/list', params, () => Mock.mockGetImportLogList(), () =>
      request({ path: '/k2/management/device_disabled_record/import/logs/list', method: Method.POST, body: params })
    );
  }

  public async getErrorFileUrl(
    taskId: string
  ): Promise<ApiResponse<ErrorFileData>> {
    return withMock('import/logs/errors', { taskId }, () => Mock.MOCK_ERROR_FILE, () =>
      request({ path: '/k2/management/device_disabled_record/import/logs/errors', method: Method.POST, body: { taskId } })
    );
  }
}

export default new AbnormalDurationApi();
